"use client";
import Image from "@/components/Atoms/Image";
import { LazyMotion, domAnimation, m, motion } from "framer-motion";
import { cn, getProductImageAlt, getProductImageSrc, slideReveal, string_to_slug } from "@/lib/functions";
import styles from "./index.module.scss";
import { FullBackgroundImage } from "@/components/Molecules/ImageCard";
import { TextCopyCard } from "@/components/Molecules/CopyCard";
import {
  AboveParallaxImage,
  BellowParallaxImage,
  CenterParallaxImage,
  StyleBackgroundImage,
} from "@/components/Atoms/ParallaxImage";
import { Shape } from "@/components/Atoms/Spacer";
import React from "react";
import GeoWrapper from "@/components/Templates/Main/GeoConditional";
import { cva, VariantProps } from "class-variance-authority";
import { TextImageProps } from "types/types";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import {
  getTextImageLabel,
  getTextImageLabelColor,
} from "./textImageLabel";

export type TextImageHalfVariantProps = VariantProps<
  typeof TextImageHalfVariants
>;
const TextImageHalfVariants = cva(` mx-auto relative w-full `, {
  variants: {
    contentHeight: {
      small: "",
      medium: "min-h-[600px]",
      large: "min-h-[800px]",
      default: "overflow-x-hidden",
      textSmall: "",
    },
    useSectionMargins: {
      true: "py-8 md:py-20",
      false: "py-0  pt-4 lg:pt-0",
    },
    useExtraTopMargins: {
      true: "pt-20 pb-12 md:pt-24 md:pb-12",
      false: "",
    },
    useExtraPadding: {
      true: "pb-24 lg:pb-32",
      false: "",
    },
  },
  defaultVariants: {
    contentHeight: "default",
    useSectionMargins: false,
    useExtraTopMargins: false,
    useExtraPadding: false,
  },
});

interface TextImageHalfProps
  extends VariantProps<typeof TextImageHalfVariants> {
  data: TextImageProps;
  radius?: "rounded" | "roundedXl" | "none";
  contentHeight?: "small" | "medium" | "large" | "default" | "textSmall";
  useSectionMargins?: boolean;
  useExtraTopMargins?: boolean;
  useExtraPadding?: boolean;
  buy?: any;
  layout?: "fullWidth" | "container" | "card";
}

const textImageHalfVariants = {
  container: cva("", {
    variants: {
      contentHeight: {
        small: "pt-12 sm:translate-y-[-18%] md:translate-y-[0] lg:h-[450px]",
        medium: "",
        large: "",
        default: "",
        textSmall: "",
      },
    },
  }),

  innerContainer: cva("relative w-full", {
    variants: {
      hasParallax: {
        true: "",
        false: "",
      },
      alignment: {
        left: "",
        right: "",
      },
      parallaxPos: {
        above: "",
        bellow: "",
        center: "",
      },
    },
    compoundVariants: [
      {
        hasParallax: true,
        alignment: "right",
        class: "lg:pl-[5rem]",
      },
      {
        hasParallax: true,
        alignment: "left",
        class: "lg:pl-[5rem]",
      },
      {
        hasParallax: true,
        alignment: "right",
        parallaxPos: "above",
        class: "lg:pl-[20rem]",
      },
    ],
  }),

  imageContainer: cva("flex-none lg:col-span-1 flex z-20 w-full self-stretch", {
    variants: {
      alignment: {
        left: "order-1 lg:order-2",
        right: "order-1",
      },
      layout: {
        fullWidth: "",
        container: "",
        card: "",
      },
      contentHeight: {
        small: "translate-y-[0] xl:translate-y-[-25%] justify-center flex",
        medium: "",
        large: "",
        default: "",
        textSmall: "",
      },
    },
    compoundVariants: [
      {
        layout: "fullWidth",
        alignment: "right",
        class: "lg:translate-x-[-10vw]",
      },
      {
        layout: "fullWidth",
        alignment: "left",
        class: "lg:translate-x-[10vw]",
      },
    ],
  }),

  imageWrapper: cva("relative", {
    variants: {
      contentHeight: {
        small: "max-w-[30rem] lg:max-w-[33rem] relative bottom-0",
        medium: "",
        large: "",
        default: "",
        textSmall: "",
      },
    },
  }),

  gridLayout: cva("flex lg:grid relative mx-auto w-full", {
    variants: {
      columnDisplay: {
        default: "flex-col",
        reverse: "flex-col-reverse",
      },
      gridCols: {
        default: "lg:grid-cols-2",
        custom: "lg:grid-cols-[450px_auto]",
      },
      layout: {
        fullWidth: "container 2xl:max-w-[1360px] gap-12 lg:gap-16 lg:items-stretch",
        container: "container 2xl:max-w-[1360px] gap-12 lg:gap-16 lg:items-stretch",
        card: "container 2xl:max-w-[1360px] gap-0 overflow-hidden lg:items-stretch",
      },
    },
    defaultVariants: {
      columnDisplay: "default",
      gridCols: "default",
    },
  }),

  imageAsset: cva("", {
    variants: {
      height: {
        reduced: "md:max-w-[400px] m-auto",
        default: "",
      },
    },
    defaultVariants: {
      height: "default",
    },
  }),

  image: cva("", {
    variants: {
      format: {
        png: "",
        default: "",
      },
      radius: {
        rounded: "rounded-md",
        roundedXl: "rounded-3xl",
        none: "",
      },
      hasParallax: {
        true: styles.parallaxMain,
        false: "",
      },
      fit: {
        cover: styles.imageFix,
        contain: styles.imageContain,
      },
    },
    defaultVariants: {
      format: "default",
      radius: "none",
      hasParallax: false,
      fit: "cover",
    },
  }),

  label: cva("", {
    variants: {
      format: {
        png: "text-center md:px-24",
        default: "text-left",
      },
    },
    defaultVariants: {
      format: "default",
    },
  }),
};

export default function TextImageHalf({
  data,
  contentHeight,
  useSectionMargins,
  useExtraTopMargins,
  useExtraPadding,
  radius,
  buy,
  layout,
}: TextImageHalfProps) {
  const geoFilter = data?.geo?.map((filter: any) => filter);
  const resolvedLayout = (data.layout ?? layout)?.toLowerCase?.();
  const isCardLayout = resolvedLayout === "card";
  const isContainerLayout = resolvedLayout === "container";
  const textBackgroundOnly = data.backgroundType === "container" || isCardLayout;
  const imageLabel = getTextImageLabel(data);
  const imageLabelColor = getTextImageLabelColor(data);
  const cardShellRadius = (() => {
    if (radius === "roundedXl") return "rounded-3xl";
    if (radius === "none") return "";
    return "rounded-md";
  })();

  const cardTextColumnRadius = (() => {
    const mobileBottom =
      radius === "roundedXl"
        ? "max-lg:rounded-b-3xl"
        : radius === "none"
          ? ""
          : "max-lg:rounded-b-md";

    if (radius === "roundedXl") {
      return cn(
        mobileBottom,
        data.textAlignment === "right"
          ? "lg:rounded-br-3xl lg:rounded-tr-3xl lg:rounded-tl-none lg:rounded-bl-none"
          : "lg:rounded-tl-3xl lg:rounded-bl-3xl lg:rounded-tr-none lg:rounded-br-none"
      );
    }

    if (radius === "rounded") {
      return cn(
        mobileBottom,
        data.textAlignment === "right"
          ? "lg:rounded-br-md lg:rounded-tr-md lg:rounded-tl-none lg:rounded-bl-none"
          : "lg:rounded-tl-md lg:rounded-bl-md lg:rounded-tr-none lg:rounded-br-none"
      );
    }

    return mobileBottom;
  })();

  const cardImageColumnRadius = (() => {
    const mobileTop =
      radius === "roundedXl"
        ? "max-lg:rounded-t-3xl"
        : radius === "none"
          ? ""
          : "max-lg:rounded-t-md";

    if (radius === "roundedXl") {
      return cn(
        mobileTop,
        data.textAlignment === "right"
          ? "lg:rounded-tl-3xl lg:rounded-bl-3xl lg:rounded-tr-none lg:rounded-br-none"
          : "lg:rounded-tr-3xl lg:rounded-br-3xl lg:rounded-tl-none lg:rounded-bl-none"
      );
    }

    if (radius === "rounded") {
      return cn(
        mobileTop,
        data.textAlignment === "right"
          ? "lg:rounded-tl-md lg:rounded-bl-md lg:rounded-tr-none lg:rounded-br-none"
          : "lg:rounded-tr-md lg:rounded-br-md lg:rounded-tl-none lg:rounded-bl-none"
      );
    }

    return mobileTop;
  })();
  const textOrderClass =
    data.textAlignment === "right" ? styles.textRight : styles.textLeft;
  const imageOrderClass =
    data.textAlignment === "left" ? "order-1 lg:order-2" : "order-1 lg:order-1";
  const parallaxPosition = data.parallaxImagePosition?.toLowerCase?.() ?? "above";
  const isCenterParallax =
    Boolean(data.addOverlayParallaxImage) &&
    parallaxPosition === "center" &&
    Boolean(data.parallaxImage);
  const hasParallaxBottle =
    Boolean(data.addOverlayParallaxImage) && Boolean(data.parallaxImage);
  const isCopyOnLeft = data.textAlignment !== "right";
  const sectionBackgroundHex = isCardLayout
    ? data.sectionBgColor?.color?.hex ?? data?.bgColor?.color?.hex
    : data?.bgColor?.color?.hex;
  const halfReducedCardStyle = {
    "--bg": data?.bgColor?.color?.hex,
    "--copy-text-color":
      data.textColor?.color?.hex || data.titleColor?.color?.hex,
  } as React.CSSProperties;
  const isReducedAssetHeight = data.mainAssetHeight === "reduced";

  const renderInlineParallax = () => {
    if (!data.addOverlayParallaxImage || !data.parallaxImage || isCenterParallax) {
      return null;
    }

    return parallaxPosition === "bellow" ? (
      <BellowParallaxImage
        position={data.textAlignment}
        data={data.parallaxImage}
      />
    ) : (
      <AboveParallaxImage
        position={data.textAlignment}
        data={data.parallaxImage}
      />
    );
  };

  return (
    <GeoWrapper
      list={geoFilter?.[0]?.listOfCountries}
      country={geoFilter?.[0]?.countries}
      initial={geoFilter?.[0]?.countryAvailability}
    >
      {data.addSpacer && (
        <Shape
          shape={data.spacerConfiguration?.customShape}
          color={isCardLayout ? data.sectionBgColor ?? data.bgColor : data.bgColor}
          direction={data.spacerConfiguration?.spacerDirection}
          position={
            data.spacerConfiguration?.spacerPosition === "invert"
              ? undefined
              : data.spacerConfiguration?.spacerPosition || "bottom"
          }
          icon={[
            data.spacerConfiguration?.addIconElement,
            data?.spacerConfiguration?.spacerIcon,
          ]}
          iconPosition={data.spacerConfiguration?.iconPosition}
          invert={false}
        />
      )}

      <div
        style={{ backgroundColor: sectionBackgroundHex }}
        className={cn(
          TextImageHalfVariants({
            contentHeight,
            useSectionMargins,
            useExtraTopMargins,
            useExtraPadding,
          })
        )}
      >
        {data.backgroundType == "full" && !isCardLayout && (
          <>
            {data.addBackground && data.backgroundImage && (
              <>
                {data.backgroundImage.format === "svg" ? (
                  <StyleBackgroundImage
                    position={data.textAlignment}
                    data={data.backgroundImage}
                  />
                ) : (
                  <FullBackgroundImage data={data.backgroundImage} />
                )}
              </>
            )}
          </>
        )}
        <div
          className="absolute top-[-100px]"
          id={data.title ? string_to_slug(data.title) : ""}
        ></div>
        <div
          className={cn(
            textImageHalfVariants.container({
              contentHeight: isCardLayout ? "default" : data.contentHeight,
            })
          )}
        >
          {isCardLayout ? (
            <div className="w-full flex justify-center">
              <div
                style={halfReducedCardStyle}
                className={cn(
                  "w-full max-w-[1000px] mx-auto flex flex-col lg:grid lg:grid-cols-2 lg:items-stretch",
                  cardShellRadius,
                  styles.cardHalfReducedShell
                )}
              >
                <div
                  style={{
                    backgroundColor: data?.bgColor?.color?.hex,
                    ...halfReducedCardStyle,
                  }}
                  className={cn(
                    styles.cardImageColumnHalfReduced,
                    imageOrderClass,
                    cardImageColumnRadius,
                    "relative max-lg:z-20 lg:z-10"
                  )}
                >
                  <div
                    className={cn(
                      "flex h-full min-h-0 w-full flex-col",
                      data.showLabel && imageLabel && "gap-2"
                    )}
                  >
                    <div className={styles.cardImageMobileWrapper}>
                      <div
                        className={styles.cardImageMobileFrame}
                        style={halfReducedCardStyle}
                      >
                        {data.image && (
                          <Image
                            data={data.image}
                            className={styles.cardFeatureImage}
                            objectFit="cover"
                          />
                        )}
                        {hasParallaxBottle && (
                          <div
                            className={styles.cardImageGradient}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                      {hasParallaxBottle && (
                        <div className={styles.cardParallaxMobileHero}>
                          <img
                            src={getProductImageSrc(data.parallaxImage)}
                            alt={getProductImageAlt(data.parallaxImage)}
                            className={styles.cardParallaxMobileHeroImage}
                            loading="lazy"
                          />
                        </div>
                      )}
                    </div>
                    {data.showLabel && imageLabel && (
                      <div className={styles.cardImageLabel}>
                        <MultiPurposeParagraph
                          type="image"
                          data={imageLabel}
                          color={imageLabelColor}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: data?.bgColor?.color?.hex,
                    ...halfReducedCardStyle,
                  }}
                  className={cn(
                    styles.cardColumn,
                    textOrderClass,
                    cardTextColumnRadius,
                    "relative z-10"
                  )}
                >
                  {textBackgroundOnly &&
                    data.addBackground &&
                    data.backgroundImage && (
                      <>
                        {data.backgroundImage.format === "svg" ? (
                          <StyleBackgroundImage
                            position={data.textAlignment}
                            data={data.backgroundImage}
                          />
                        ) : (
                          <FullBackgroundImage data={data.backgroundImage} />
                        )}
                      </>
                    )}
                  <div
                    className={cn(
                      styles.cardCopyBlock,
                      hasParallaxBottle &&
                        data.textAlignment === "left" &&
                        styles.cardCopyAlignLeft,
                      hasParallaxBottle &&
                        data.textAlignment === "right" &&
                        styles.cardCopyAlignRight,
                      !hasParallaxBottle && styles.cardCopyBlockNoParallax
                    )}
                  >
                    <TextCopyCard
                      layout="left"
                      ctaLayout={data.ctaLayout}
                      data={data}
                      buy={buy}
                      cart={buy?.[3]}
                    />
                  </div>
                </div>

                {hasParallaxBottle && (
                  <div
                    className={cn(
                      styles.cardParallaxLayer,
                      styles.cardParallaxLayerDesktop
                    )}
                  >
                    <img
                      src={getProductImageSrc(data.parallaxImage)}
                      alt={getProductImageAlt(data.parallaxImage)}
                      className={styles.cardParallaxImage}
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              className={cn(
                textImageHalfVariants.gridLayout({
                  columnDisplay: data.mobileColumnDisplay ? "reverse" : "default",
                  layout: data.layout,
                  gridCols:
                    data.contentHeight === "textSmall" &&
                    data.textAlignment === "left"
                      ? "custom"
                      : "default",
                }),
                isCenterParallax && "overflow-visible",
                resolvedLayout === "container" &&
                  data.backgroundType === "container" &&
                  "py-6 lg:py-0"
              )}
            >
            <div
              className={cn(
                textImageHalfVariants.innerContainer({
                  hasParallax: data?.addOverlayParallaxImage,
                  alignment: data.textAlignment === "left" ? "left" : "right",
                  parallaxPos: data.parallaxImagePosition,
                }),
                styles.innerTextContainer,
                data.useSmallerContainer && styles.smallerContainer,
                data.textAlignment === "right"
                  ? styles.textRight
                  : styles.textLeft,
                data.contentHeight === "small" && styles.smallInnerContainer
              )}
            >
              {textBackgroundOnly && (
                <>
                  {data.addBackground && data.backgroundImage && (
                    <>
                      {data.backgroundImage.format === "svg" ? (
                        <StyleBackgroundImage
                          position={data.textAlignment}
                          data={data.backgroundImage}
                        />
                      ) : (
                        <FullBackgroundImage data={data.backgroundImage} />
                      )}
                    </>
                  )}
                </>
              )}

              <TextCopyCard
                layout="left"
                ctaLayout={data.ctaLayout}
                data={data}
                buy={buy}
                cart={buy?.[3]}
              />
            </div>

            <div
              className={cn(
                textImageHalfVariants.imageContainer({
                  alignment: data.textAlignment === "left" ? "left" : "right",
                  contentHeight: data.contentHeight,
                  layout: data.layout,
                }),
                styles.halfImageColumn
              )}
            >
              <LazyMotion features={domAnimation}>
                <m.div
                  className={cn(
                    textImageHalfVariants.imageWrapper({
                      contentHeight: data.contentHeight,
                    }),
                    "flex h-full w-full flex-row justify-center gap-12 lg:flex-col",
                    styles.halfImageStretch
                  )}
                >
                  <motion.div
                    variants={slideReveal(data.textAlignment)}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    exit="exit"
                    className="relative flex h-full w-full flex-col"
                  >
                    <div
                      className={cn(
                        textImageHalfVariants.imageAsset({
                          height:
                            data.mainAssetHeight === "reduced"
                              ? "reduced"
                              : "default",
                        }),
                        "flex min-h-0 w-full flex-col",
                        data.showLabel && imageLabel ? "gap-2" : "flex-1"
                      )}
                    >
                      <div
                        className={cn(
                          styles.halfImageMedia,
                          "min-h-0 w-full",
                          !(data.showLabel && imageLabel) && "flex-1"
                        )}
                      >
                        {data.image && (
                          <Image
                            data={data.image}
                            objectFit={
                              isContainerLayout && !data.parallaxImage
                                ? "contain"
                                : "cover"
                            }
                            className={cn(
                              textImageHalfVariants.image({
                                format:
                                  data.image.format === "png" ? "png" : "default",
                                radius,
                                hasParallax: !!data.parallaxImage,
                                fit:
                                  isContainerLayout && !data.parallaxImage
                                    ? "contain"
                                    : "cover",
                              }),
                              "h-full w-full"
                            )}
                          />
                        )}
                      </div>

                      {data.showLabel && imageLabel && (
                        <div className="w-full shrink-0">
                          <MultiPurposeParagraph
                            type="image"
                            data={imageLabel}
                            color={imageLabelColor}
                          />
                        </div>
                      )}
                    </div>
                    {renderInlineParallax()}
                  </motion.div>
                </m.div>
              </LazyMotion>
            </div>

            {isCenterParallax && (
              <CenterParallaxImage data={data.parallaxImage} />
            )}
          </div>
          )}
        </div>
      </div>
    </GeoWrapper>
  );
}
